import { useCallback, useEffect, useRef } from 'react';
import { CHATTEASER3_ANIM_PHASE, TAB_ANIM_PRESET } from './chatteaser3Anim';

type AnimPhaseLocal = (typeof CHATTEASER3_ANIM_PHASE)[keyof typeof CHATTEASER3_ANIM_PHASE];

export function useChatteaser3AnimationTimers({
  activeTab,
  displayedTab,
  enterMs = TAB_ANIM_PRESET.enterMs,
  exitMs = TAB_ANIM_PRESET.exitMs,
  scheduleDisplayedTab,
  setAnimPhase,
}: {
  activeTab: number;
  displayedTab: number;
  enterMs?: number;
  exitMs?: number;
  scheduleDisplayedTab: (value: number) => void;
  setAnimPhase: (value: AnimPhaseLocal) => void;
}) {
  const enterTimerRef = useRef<number | null>(null);
  const exitTimerRef = useRef<number | null>(null);

  const clearEnterTimer = useCallback((): void => {
    if (enterTimerRef.current == null) return;
    window.clearTimeout(enterTimerRef.current);
    enterTimerRef.current = null;
  }, []);
  const clearExitTimer = useCallback((): void => {
    if (exitTimerRef.current == null) return;
    window.clearTimeout(exitTimerRef.current);
    exitTimerRef.current = null;
  }, []);
  const scheduleEnterIdle = useCallback((): void => {
    clearEnterTimer();
    enterTimerRef.current = window.setTimeout(() => {
      setAnimPhase(CHATTEASER3_ANIM_PHASE.Idle);
      enterTimerRef.current = null;
    }, enterMs);
  }, [clearEnterTimer, enterMs, setAnimPhase]);

  useEffect(() => {
    scheduleEnterIdle();
  }, [scheduleEnterIdle]);

  useEffect(() => {
    if (activeTab === displayedTab) return;
    clearExitTimer();
    exitTimerRef.current = window.setTimeout(() => {
      scheduleDisplayedTab(activeTab);
      setAnimPhase(CHATTEASER3_ANIM_PHASE.Enter);
      scheduleEnterIdle();
      exitTimerRef.current = null;
    }, exitMs);
    return clearExitTimer;
  }, [activeTab, clearExitTimer, displayedTab, exitMs, scheduleDisplayedTab, scheduleEnterIdle, setAnimPhase]);

  useEffect(() => {
    return () => {
      clearEnterTimer();
      clearExitTimer();
    };
  }, [clearEnterTimer, clearExitTimer]);

  return {
    clearEnterTimer,
    clearExitTimer,
  };
}
